import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
    AlertTriangle,
    Check,
    CircleDot,
    CopyPlus,
    ExternalLink,
    FileType2,
    FilePlus2,
    GitCompare,
    ShieldCheck,
    ScanText,
    FolderOpen,
    HardDrive,
    Info,
    Loader2,
    Monitor,
    PencilLine,
    Save,
    X,
    type LucideIcon
} from "@fg/icons";
import { useAccessToken } from "@/auth/useAccessToken";
import { canSaveInPlace, driveApi, isDriveUrl, type DriveItem } from "@/api/driveApi";
import { cn } from "@/lib/utils";
import { ShellHeaderPortal } from "@/features/shell/ShellHeader";
import type { PdfViewerHandle } from "./PdfViewerHost";
import { DrivePickerDialog, OpenChoiceDialog, SaveAsDialog, type SaveAsChoice } from "./DriveDialogs";
import { CommentsButton, CommentsPanel, isCommentsShortcut, useOpenCommentCount } from "./comments/CommentsPanel";
import { pdfCommentsAdapter } from "./comments/pdfComments";
import { EditToolbar, ModeSwitch } from "./edit/EditToolbar";
import { FreeTextMiniToolbar } from "./edit/FreeTextMiniToolbar";
import { ImageMiniToolbar, type ImageLook, type Stacking } from "./edit/ImageMiniToolbar";
import { ContextMenu, type ContextItem } from "./edit/ContextMenu";
import { FormBuilder } from "./edit/FormBuilder";
import { CompareDialog } from "./edit/CompareDialog";
import { DigitalSignDialog } from "./edit/DigitalSignDialog";
import { OcrDialog } from "./edit/OcrDialog";
import { NewPdfDialog, PageMarksDialog, RedactDialog, TableSizeDialog, type NewPdfChoice } from "./edit/NewPdfDialog";
import { PAGE_SIZES, createBlankPdf } from "@fg/web-core/pdf";
import { DEFAULT_FORM_STYLE, type FieldInfo, type FormStyle, type PaletteKind } from "./edit/formSets";
import {
    EDIT_COVER_SUBJECT,
    type DrawTool,
    type EditTool,
    type FreeTextStyle,
    type PageRect,
    type PdfMode,
    type ShapeKind,
    type ViewerChange
} from "./edit/editTypes";

import { useAuth } from "react-oidc-context";
import { TRIGGERS, cropPixels, cutOutBackdrop, safeUri, type DataLink, type EventMap, type PdfAction, type Trigger } from "@fg/web-core/pdf";
import { PropertiesPane } from "./props/PropertiesPane";
import { annotRows, documentRows, eventRows, fieldRows, linkRows, objectRows, pageRows, parseColor, parseLen, triggersFor, UNITS, VALIDATION_CHOICES, type PropRow, type PropTab, type Unit } from "./props/rows";
import { DocumentFontsDialog } from "./edit/DocumentFontsDialog";
import { VerifyFillDialog } from "./edit/FillDialogs";
import { ActionsEditor, DataLinkDialog, SecretsDialog } from "./edit/LinkDialogs";
import { googleFontsOn, setGoogleFonts } from "./edit/fontres";
import { listSecrets, snapshot as secretsSnapshot } from "./edit/secrets";
import { fetchPicture, loadValues, saveValues, testLink, uploadPicture, type RunEnv } from "./edit/dataRun";
import { beep, buildSubmit, evalSetValue, sendRequest } from "./edit/actionRun";
import { pixelsToPng, pixelsToUrl } from "./edit/pixels";
import type { ObjectSelectionInfo } from "./edit/editTypes";
import { PanelRight } from "@fg/icons";

const PdfViewerHost = lazy(() =>
    import("./PdfViewerHost").then((m) => ({ default: m.PdfViewerHost }))
);

/** clean = nothing to save; dirty = annotations/forms changed since open or the last save. */
type SaveStatus = "clean" | "dirty" | "saving" | "failed";

interface OpenDoc {
    name: string;
    /** The Drive file it was opened from / last saved to; null = a local file. */
    drive: DriveItem | null;
}

type DialogState =
    | { kind: "digital-sign" }
    | { kind: "ocr" }
    | { kind: "compare" }
    | { kind: "marks" }
    | { kind: "redact" }
    | { kind: "table-size" }
    | { kind: "new-pdf" }
    | { kind: "open-choice" }
    | { kind: "drive-picker" }
    | { kind: "save-as"; target: "drive" | "computer" }
    | { kind: "fonts" }
    | { kind: "verify" }
    | { kind: "secrets" }
    | { kind: "events"; trigger: Trigger; target: { type: "field"; id: string } | { type: "link"; num: number; page: number } }
    | { kind: "datalink"; scope: { type: "form" } | { type: "field"; id: string } }
    | { kind: "crop"; id: string };

/** What the Properties pane is showing. */
type PropSel =
    | { kind: "none" }
    | { kind: "object"; info: ObjectSelectionInfo }
    | { kind: "annot"; id: string }
    | { kind: "field"; id: string }
    | { kind: "link"; num: number; page: number };

const lsGet = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string | null) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* not kept */ } };

const DRIVE_WEB_URL = (import.meta.env.VITE_DRIVE_WEB_URL ?? "").trim() || "http://localhost:5240";

/** Images placed with Edit → Image. */
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

function messageOf(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

function plural(n: number, word: string): string {
    return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/**
 * Reader: view, annotate, fill and sign a PDF, then save it back to
 * Drive. `?fileId=<id>` opens a Drive file (Drive's "Open in PDF editor")
 * and stays in the URL for whatever file is open. Saving writes a new
 * version when the user owns or edits the file, otherwise Save As makes a
 * copy in a My Drive folder they choose.
 *
 * View / Edit: View is the viewer as it always was. Edit adds the red
 * "Editing" bar — Edit text (cover + new text), Add text, Image, Shape, Sign,
 * Form fields, Pages — counts the edits, and Save burns them into the page
 * (Drive BFF POST /api/pdf/flatten) before saving the new version. Leaving
 * Edit with edits discards them.
 */
export function PdfPage() {
    const [params, setParams] = useSearchParams();
    const fileIdParam = params.get("fileId");
    const openFileUrl = params.get("openFile");
    const openFileName = params.get("fileName");
    const token = useAccessToken();
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const hasToken = Boolean(token);

    const viewerRef = useRef<PdfViewerHandle>(null);
    const [viewerReady, setViewerReady] = useState(false);
    const [doc, setDoc] = useState<OpenDoc | null>(null);
    const docRef = useRef(doc);
    docRef.current = doc;
    const [status, setStatus] = useState<SaveStatus>("clean");
    const statusRef = useRef(status);
    statusRef.current = status;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialog, setDialog] = useState<DialogState | null>(null);
    const auth = useAuth();
    const who = (auth?.user?.profile?.email as string | undefined) ?? (auth?.user?.profile?.preferred_username as string | undefined) ?? (auth?.user?.profile?.sub as string | undefined) ?? "";
    // Properties (F4), the unit lengths are shown in, and what is selected
    const [propsOpen, setPropsOpen] = useState(() => lsGet("fg.pdf.props") === "1");
    const [propsW, setPropsW] = useState(() => Number(lsGet("fg.pdf.props.w")) || 320);
    const [propTab, setPropTab] = useState<PropTab>("Properties");
    const [unit, setUnit] = useState<Unit>(() => (["pt", "mm", "in"] as const).find((u) => u === lsGet("fg.pdf.unit")) ?? "pt");
    const [propSel, setPropSel] = useState<PropSel>({ kind: "none" });
    const [bump, setBump] = useState(0);
    const [secretCount, setSecretCount] = useState(0);
    const [autosave, setAutosave] = useState(() => lsGet("fg.pdf.autosave") === "1");
    const [autoNote, setAutoNote] = useState<string | null>(null);
    const canEditRef = useRef(false);

    /** Drive id whose bytes are in the viewer (or were last attempted). */
    const loadedIdRef = useRef<string | null>(null);
    /** Loading a document fires annotation events of its own — not user edits. */
    const ignoreChangesUntil = useRef(0);
    const changedWhileSaving = useRef(false);
    const urlOpened = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /** Bumped by every Drive / URL open and by Close — a stale open drops its result. */
    const openSeqRef = useRef(0);
    /** Settles when the save in flight finishes — Close waits for it. */
    const saveInFlightRef = useRef<Promise<void> | null>(null);

    /** Keeps ?fileId= pointing at the open Drive file (and drops the one-shot openFile params). */
    const setFileParam = useCallback(
        (id: string | null) => {
            setParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (id) next.set("fileId", id);
                    else next.delete("fileId");
                    next.delete("openFile");
                    next.delete("fileName");
                    return next;
                },
                { replace: true }
            );
        },
        [setParams]
    );

    // ---------- Edit mode state ----------

    const [mode, setMode] = useState<PdfMode>("view");
    const modeRef = useRef<PdfMode>("view");
    const [edits, setEdits] = useState(0);
    const editsRef = useRef(0);
    const [tool, setToolState] = useState<EditTool>("none");
    const toolRef = useRef<EditTool>("none");
    const [formOpen, setFormOpen] = useState(false);
    useEffect(() => { viewerRef.current?.setFieldEditing(mode === "edit" && formOpen); }, [mode, formOpen, viewerReady]);
    const [armed, setArmed] = useState<PaletteKind | null>(null);
    const [formStyle, setFormStyleState] = useState<FormStyle>(DEFAULT_FORM_STYLE);
    const [redactCount, setRedactCount] = useState(0);
    const [fieldSel, setFieldSel] = useState<FieldInfo | null>(null);
    const [hint, setHint] = useState<string | null>(null);
    /** The text box selected in Edit mode — the mini toolbar follows it. */
    const [selected, setSelected] = useState<{ id: string; style: FreeTextStyle } | null>(null);
    /** Edit text pairs, both ways: cover id ↔ new-text id. Removing one removes the other. */
    const pairsRef = useRef(new Map<string, string>());
    /** An Edit text pair being added — its ids may arrive as annotationAdd events. */
    const pendingPairRef = useRef<{ coverId: string | null; textId: string | null; until: number } | null>(null);
    /** Events from our own programmatic changes until then — whoever made them counts them once. */
    const suppressUntil = useRef(0);
    /** The page organizer was opened from Edit — its Save reloads the document. */
    const pagesPendingRef = useRef(false);
    const [history, setHistory] = useState({ canUndo: false, canRedo: false });
    /** The bytes in the viewer as opened or last saved — Discard reloads them. */
    const lastBlobRef = useRef<Blob | null>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);

    const setTool = useCallback((t: EditTool) => {
        toolRef.current = t;
        setToolState(t);
    }, []);

    const markDirty = useCallback(() => {
        if (!docRef.current) return;
        if (statusRef.current === "saving") {
            changedWhileSaving.current = true;
            return;
        }
        statusRef.current = "dirty";
        setStatus("dirty");
    }, []);

    /** One more edit on the "N edits" counter (Edit mode only). */
    const countEdit = useCallback(() => {
        if (modeRef.current !== "edit") return;
        editsRef.current += 1;
        setEdits(editsRef.current);
    }, []);

    /** Back to View without touching the document (the caller reloads it when edits must go). */
    const exitEditState = useCallback(() => {
        if (modeRef.current === "edit") viewerRef.current?.exitEditing();
        modeRef.current = "view";
        setMode("view");
        editsRef.current = 0;
        setEdits(0);
        setTool("none");
        setFormOpen(false);
        setHint(null);
        setSelected(null);
        pairsRef.current.clear();
        pendingPairRef.current = null;
        pagesPendingRef.current = false;
    }, [setTool]);

    /** False when `isCurrent` says the open was abandoned (Close) before the bytes reached the viewer. */
    const showInViewer = useCallback(
        async (blob: Blob, isCurrent: () => boolean = () => true) => {
            // Drive may label the bytes octet-stream; the viewer wants a PDF data URL.
            const pdf = new Blob([blob], { type: "application/pdf" });
            const base64 = await blobToBase64(pdf);
            if (!isCurrent()) return false;
            exitEditState();
            lastBlobRef.current = pdf;
            ignoreChangesUntil.current = Date.now() + 2000;
            viewerRef.current?.loadBase64(base64);
            return true;
        },
        [exitEditState]
    );

    const openFromDrive = useCallback(
        async (id: string) => {
            const seq = ++openSeqRef.current;
            const isCurrent = () => seq === openSeqRef.current;
            loadedIdRef.current = id;
            setError(null);
            setLoading(true);
            try {
                const meta = await driveApi.getItem(id, tokenRef.current);
                if (!isCurrent()) return;
                if (meta.kind !== "File") throw new Error(`“${meta.name}” is a folder — pick a PDF file.`);
                if (meta.canDownload === false) {
                    throw new Error(`The owner turned off downloading for “${meta.name}”, so it can't open in the PDF editor. Preview it in Drive instead.`);
                }
                const blob = await driveApi.downloadBlob(id, tokenRef.current);
                if (!(await showInViewer(blob, isCurrent))) return;
                setDoc({ name: meta.name, drive: meta });
                setStatus("clean");
                setFileParam(id);
            } catch (err) {
                if (isCurrent()) setError(messageOf(err));
            } finally {
                if (isCurrent()) setLoading(false);
            }
        },
        [showInViewer, setFileParam]
    );

    // ?fileId= (and the older ?openFile=&fileName= hand-off) once the viewer is up.
    useEffect(() => {
        if (!viewerReady || !hasToken) return;
        if (fileIdParam) {
            if (loadedIdRef.current !== fileIdParam) void openFromDrive(fileIdParam);
            return;
        }
        if (openFileUrl && !urlOpened.current) {
            urlOpened.current = true;
            const seq = ++openSeqRef.current;
            const isCurrent = () => seq === openSeqRef.current;
            void (async () => {
                setError(null);
                setLoading(true);
                try {
                    // The bearer token only ever goes to the Drive BFF.
                    const headers = new Headers();
                    if (tokenRef.current && isDriveUrl(openFileUrl)) headers.set("Authorization", `Bearer ${tokenRef.current}`);
                    const res = await fetch(openFileUrl, { headers, credentials: "omit" });
                    if (!res.ok) throw new Error(`Couldn't fetch the file (${res.status} ${res.statusText})`);
                    if (!(await showInViewer(await res.blob(), isCurrent))) return;
                    setDoc({ name: openFileName ?? "document.pdf", drive: null });
                    setStatus("clean");
                } catch (err) {
                    if (isCurrent()) setError(messageOf(err));
                } finally {
                    if (isCurrent()) setLoading(false);
                }
            })();
        }
    }, [viewerReady, hasToken, fileIdParam, openFileUrl, openFileName, openFromDrive, showInViewer]);

    // ---------- Edit tools ----------

    const linkPendingPair = useCallback(() => {
        const p = pendingPairRef.current;
        if (!p?.coverId || !p.textId) return;
        pairsRef.current.set(p.coverId, p.textId);
        pairsRef.current.set(p.textId, p.coverId);
        pendingPairRef.current = null;
    }, []);

    /** Edit text: cover `rect` in white and put an editable text box with `text` on top. One edit. */
    const createPair = useCallback(
        (rect: PageRect, text: string, lineHeight: number) => {
            const v = viewerRef.current;
            if (!v) return;
            const now = Date.now();
            suppressUntil.current = now + 800;
            pendingPairRef.current = { coverId: null, textId: null, until: now + 1500 };
            const ids = v.addCoverAndText(rect, text, lineHeight);
            const p = pendingPairRef.current;
            if (p) {
                p.coverId ??= ids.coverId;
                p.textId ??= ids.textId;
                linkPendingPair();
            }
            v.setTool("None");
            setTool("none");
            markDirty();
            countEdit();
            setHint("Double-click the new text to change it. The bar above it sets font, size, bold and colour.");
            const textId = ids.textId;
            if (textId) window.setTimeout(() => viewerRef.current?.startEditing(textId), 60);
        },
        [countEdit, linkPendingPair, markDirty, setTool]
    );

    /** Edit text by dragging: the plain box just drawn becomes a cover + new text. */
    const replaceRegion = useCallback(
        (drawnId: string, rect: PageRect) => {
            const v = viewerRef.current;
            if (!v) return;
            suppressUntil.current = Date.now() + 800;
            v.setTool("None");
            v.deleteAnnotation(drawnId);
            const under = v.textUnder(rect);
            createPair(rect, under.text, under.lineHeight);
        },
        [createPair]
    );

    /** Edit mode, click on the page's own text: that paragraph becomes a text box, open for typing (Acrobat's Edit PDF). */
    const onEditPageText = useCallback(
        (rect: PageRect) => {
            const v = viewerRef.current;
            if (!v) return;
            const under = v.textUnder(rect);
            createPair(rect, under.text, under.lineHeight);
        },
        [createPair]
    );

    /** One placement per click: the tool lets go once the user has placed something. */
    const finishTool = useCallback(
        (resetViewer: boolean) => {
            window.setTimeout(() => {
                if (resetViewer) viewerRef.current?.setTool("None");
                setTool("none");
                setHint(null);
            }, 0);
        },
        [setTool]
    );

    const onEditText = useCallback(() => {
        const v = viewerRef.current;
        if (!v) return;
        if (toolRef.current === "edit-text") {
            v.setTool("None");
            setTool("none");
            setHint(null);
            return;
        }
        const sel = v.lastTextSelection();
        if (sel && sel.page > 0 && sel.width > 0 && sel.height > 0) {
            v.clearTextSelection();
            createPair(sel, sel.text, sel.lineHeight);
            return;
        }
        v.setTool("Rectangle", { cover: true });
        setTool("edit-text");
        setHint("Drag over the words you want to replace — or select them first, then choose Edit text.");
    }, [createPair, setTool]);

    const toggleTool = useCallback(
        (t: EditTool, drawTool: DrawTool, message: string) => {
            const v = viewerRef.current;
            if (!v) return;
            if (toolRef.current === t) {
                v.setTool("None");
                setTool("none");
                setHint(null);
                return;
            }
            v.setTool(drawTool);
            setTool(t);
            setHint(message);
        },
        [setTool]
    );

    const onAddText = useCallback(
        () => toggleTool("add-text", "FreeText", "Click on the page where the text box should go, then type."),
        [toggleTool]
    );

    const onNote = useCallback(
        () => toggleTool("note", "Note", "Click on the page where the note should go, then type it."),
        [toggleTool]
    );

    const onSpace = useCallback(
        () => toggleTool("space", "Space", "Click the line you want space after. Everything below it moves down."),
        [toggleTool]
    );

    const onRedact = useCallback(
        () => toggleTool("redact", "Redact", "Drag over the text, picture or area to remove. Marked areas stay black on the page until you choose Apply redactions."),
        [toggleTool]
    );

    const applyRedactions = useCallback(async (scrub: boolean) => {
        const v = viewerRef.current;
        setDialog(null);
        if (!v) return;
        try {
            pagesPendingRef.current = true;
            ignoreChangesUntil.current = 0;
            const n = await v.applyRedactions(scrub);
            if (!n) pagesPendingRef.current = false;
            else setHint(`Redactions applied on ${n} page${n === 1 ? "" : "s"}. Save to Drive to keep them.`);
            setRedactCount(0);
        } catch (err) {
            pagesPendingRef.current = false;
            setError(messageOf(err));
        }
    }, []);

    const applyMarks = useCallback(async (m: import("./edit/formSets").PageMarks) => {
        setDialog(null);
        try {
            const n = await viewerRef.current?.applyPageMarks(m);
            if (n) { markDirty(); countEdit(); setHint(`Added ${n} item${n === 1 ? "" : "s"} to the pages. Each one is a normal text box or image: click it to move, edit or delete it.`); }
        } catch (err) { setError(messageOf(err)); }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onTable = useCallback(() => {
        if (toolRef.current === "table") { viewerRef.current?.setTool("None"); setTool("none"); setHint(null); return; }
        setDialog({ kind: "table-size" });
    }, [setTool]);

    const onSign = useCallback(
        () => toggleTool("sign", "HandWrittenSignature", "Draw, type or upload your signature, then click where it goes."),
        [toggleTool]
    );

    const onShape = useCallback(
        (kind: ShapeKind) => {
            const v = viewerRef.current;
            if (!v) return;
            v.setTool(kind);
            setTool("shape");
            const what = kind === "Circle" ? "an ellipse" : kind === "Arrow" ? "an arrow" : `a ${kind.toLowerCase()}`;
            setHint(`Drag on the page to draw ${what}.`);
        },
        [setTool]
    );

    const onFormFields = useCallback(() => {
        const v = viewerRef.current;
        if (!v) return;
        const next = !formOpen;
        v.setTool("None");
        setTool("none");
        v.armField(null);
        setArmed(null);
        v.setFormDesigner(next);
        setFormOpen(next);
        setHint(next ? "Drag a question type from the Form builder onto the page, then set it up on the right." : null);
    }, [formOpen, setTool]);

    const onPages = useCallback(() => {
        const v = viewerRef.current;
        if (!v) return;
        v.setTool("None");
        setTool("none");
        if (!v.openPageOrganizer()) {
            setError("The page organizer isn't available.");
            return;
        }
        pagesPendingRef.current = true;
        setHint("Reorder, turn, delete or add blank pages, then choose Apply.");
    }, [setTool]);

    const onPagesClosed = useCallback(() => {
        pagesPendingRef.current = false;
        setHint(null);
    }, []);

    const onImagePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (imageInputRef.current) imageInputRef.current.value = "";
        if (!file) return;
        if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
            setError(`${file.name}: pick a PNG or JPEG image.`);
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setError(`${file.name} is over ${MAX_IMAGE_BYTES / 1024 / 1024} MB — pick a smaller image.`);
            return;
        }
        try {
            const dataUrl = await blobToBase64(file);
            const size = await imageSize(dataUrl);
            const v = viewerRef.current;
            if (!v || modeRef.current !== "edit") return;
            v.setTool("None");
            setTool("none");
            suppressUntil.current = Date.now() + 800;
            v.addImage(dataUrl, size.width, size.height, file.name);
            markDirty();
            countEdit();
            setHint("Drag the image to move it; drag a corner to resize it.");
        } catch (err) {
            setError(`Couldn't place the image: ${messageOf(err)}`);
        }
    };

    // ---------- Viewer events ----------

    const onDocumentLoad = useCallback(() => {
        ignoreChangesUntil.current = Date.now() + 600;
        window.setTimeout(() => setBump((b) => b + 1), 200);
        setSelected(null);
        if (editAfterLoad.current) {
            // a new document opens ready to build in
            editAfterLoad.current = false;
            window.setTimeout(() => enterEditRef.current(), 120);
        }
        if (pagesPendingRef.current && docRef.current) {
            // The page organizer applies its changes by reloading the document.
            pagesPendingRef.current = false;
            markDirty();
            countEdit();
        }
    }, [countEdit, markDirty]);

    const onDocumentLoadFailed = useCallback(() => {
        const name = docRef.current?.name;
        exitEditState();
        setDoc(null);
        setStatus("clean");
        setError(`Couldn't open ${name ? `“${name}”` : "this file"} — it may not be a PDF, or it may be damaged.`);
    }, [exitEditState]);

    const onDocumentChange = useCallback(
        (change: ViewerChange) => {
            const now = Date.now();
            if (now < ignoreChangesUntil.current || !docRef.current) return;
            setBump((b) => b + 1);
            // Undo and redo change what is on the page; they are not new edits to count.
            window.setTimeout(() => setRedactCount(viewerRef.current?.redactionCount() ?? 0), 0);
            if (change.kind === "history") { markDirty(); return; }
            if (change.kind === "annotationAdd" && change.annotationType === "Widget") setArmed(null);
            if (change.kind === "annotationAdd" && toolRef.current === "table") { setTool("none"); setHint(null); }
            const counted = now >= suppressUntil.current;

            const pending = pendingPairRef.current;
            if (pending && change.kind === "annotationAdd" && change.annotationId) {
                if (now > pending.until) {
                    pendingPairRef.current = null;
                } else if (change.subject === EDIT_COVER_SUBJECT) {
                    pending.coverId ??= change.annotationId;
                    linkPendingPair();
                } else if (change.annotationType === "FreeText") {
                    pending.textId ??= change.annotationId;
                    linkPendingPair();
                }
            }

            if (modeRef.current === "edit") {
                const t = toolRef.current;
                const drawnId = change.annotationId;
                const drawnRect = change.rect;
                if (
                    change.kind === "annotationAdd" && t === "edit-text" && counted &&
                    change.annotationType === "Rectangle" && change.subject !== EDIT_COVER_SUBJECT &&
                    drawnId && drawnRect
                ) {
                    // Counted once, by createPair.
                    window.setTimeout(() => replaceRegion(drawnId, drawnRect), 0);
                    return;
                }
                if (change.kind === "annotationAdd" && counted) {
                    if (t === "add-text" || t === "note" || t === "space") finishTool(false); // the new box is being typed in — leave the viewer be
                    else if (t === "shape") finishTool(true);
                }
                if (change.kind === "signature" && t === "sign") finishTool(false);
            }

            // Removing half of an Edit text pair removes the other half (one edit).
            if (change.kind === "annotationRemove" && change.annotationId) {
                const partner = pairsRef.current.get(change.annotationId);
                if (partner) {
                    pairsRef.current.delete(change.annotationId);
                    pairsRef.current.delete(partner);
                    suppressUntil.current = now + 600;
                    window.setTimeout(() => viewerRef.current?.deleteAnnotation(partner), 0);
                }
                if (selected?.id === change.annotationId) setSelected(null);
            }

            markDirty();
            if (counted) countEdit();
        },
        [countEdit, finishTool, linkPendingPair, markDirty, replaceRegion, selected]
    );

    const onViewerReady = useCallback(() => setViewerReady(true), []);

    // ---------- Mini toolbar for the selected text box ----------

    const onAnnotationSelect = useCallback((id: string | null) => {
        setFieldSel(id ? viewerRef.current?.fieldInfo(id) ?? null : null);
        setPropSel(id ? (viewerRef.current?.fieldInfo(id) ? { kind: "field", id } : { kind: "annot", id }) : { kind: "none" });
        setBump((b) => b + 1);
        if (!id || modeRef.current !== "edit") {
            setSelected(null);
            setPicture(null);
            return;
        }
        const style = viewerRef.current?.freeTextStyle(id) ?? null;
        setSelected(style ? { id, style } : null);
        const pic = style ? null : viewerRef.current?.imageInfo(id) ?? null;
        setPicture(pic ? { id, look: { opacity: pic.opacity, rotation: pic.rotation } } : null);
    }, []);

    // ---------- Mini toolbar for the selected picture ----------

    const [picture, setPicture] = useState<{ id: string; look: ImageLook } | null>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);
    const getPictureRect = useCallback(() => (picture ? viewerRef.current?.annotationClientRect(picture.id) ?? null : null), [picture]);
    const editPicture = useCallback((patch: Partial<ImageLook>) => {
        const v = viewerRef.current;
        if (!v || !picture) return;
        suppressUntil.current = Date.now() + 500;
        v.editImage(picture.id, patch);
        markDirty();
        countEdit();
        const info = v.imageInfo(picture.id);
        setPicture({ id: picture.id, look: info ? { opacity: info.opacity, rotation: info.rotation } : { ...picture.look, ...patch } });
    }, [countEdit, markDirty, picture]);
    const arrangePicture = useCallback((how: Stacking) => {
        if (!picture) return;
        suppressUntil.current = Date.now() + 500;
        viewerRef.current?.arrange(picture.id, how);
        markDirty();
        countEdit();
    }, [countEdit, markDirty, picture]);
    const removePicture = useCallback(() => {
        if (picture) viewerRef.current?.deleteAnnotation(picture.id);
        setPicture(null);
    }, [picture]);
    const onReplacePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (replaceInputRef.current) replaceInputRef.current.value = "";
        if (!file || !picture) return;
        if (!/^image\/(png|jpe?g)$/i.test(file.type)) { setError(`${file.name}: pick a PNG or JPEG image.`); return; }
        if (file.size > MAX_IMAGE_BYTES) { setError(`${file.name} is over ${MAX_IMAGE_BYTES / 1024 / 1024} MB — pick a smaller image.`); return; }
        try {
            const dataUrl = await blobToBase64(file);
            const size = await imageSize(dataUrl);
            suppressUntil.current = Date.now() + 800;
            viewerRef.current?.replaceImage(picture.id, dataUrl, size.width, size.height);
            markDirty();
            countEdit();
            setHint("Picture replaced; it keeps the same size and place.");
        } catch (err) {
            setError(messageOf(err));
        }
    };

    // ---------- Right-click menu on an added item ----------

    const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);
    const onAnnotationContextMenu = useCallback((id: string, x: number, y: number) => {
        if (modeRef.current === "edit") setMenu({ id, x, y });
    }, []);
    const menuItems = useCallback((id: string): ContextItem[] => {
        const v = viewerRef.current;
        if (!v) return [];
        const changed = () => { suppressUntil.current = Date.now() + 500; markDirty(); countEdit(); };
        const pic = v.imageInfo(id);
        const text = v.freeTextStyle(id);
        const items: ContextItem[] = [];
        if (text) items.push({ label: "Edit text", onClick: () => v.startEditing(id) });
        if (pic) {
            items.push({ label: "Replace picture…", onClick: () => { setPicture({ id, look: { opacity: pic.opacity, rotation: pic.rotation } }); replaceInputRef.current?.click(); } });
            items.push({ label: "Rotate right 90°", onClick: () => { v.editImage(id, { rotation: pic.rotation + 90 }); changed(); } });
            items.push({ label: "Rotate left 90°", onClick: () => { v.editImage(id, { rotation: pic.rotation - 90 }); changed(); } });
            if (pic.rotation) items.push({ label: "Reset rotation", onClick: () => { v.editImage(id, { rotation: 0 }); changed(); } });
            for (const pct of [100, 75, 50, 25]) {
                items.push({ label: `Opacity ${pct}%`, separator: pct === 100, disabled: Math.round(pic.opacity * 100) === pct, onClick: () => { v.editImage(id, { opacity: pct / 100 }); changed(); } });
            }
        }
        items.push({ label: "Duplicate", separator: items.length > 0, onClick: () => { if (v.duplicateAnnotation(id)) changed(); } });
        items.push({ label: "Bring to front", separator: true, onClick: () => { v.arrange(id, "front"); changed(); } });
        items.push({ label: "Bring forward", onClick: () => { v.arrange(id, "forward"); changed(); } });
        items.push({ label: "Send backward", onClick: () => { v.arrange(id, "backward"); changed(); } });
        items.push({ label: "Send to back", onClick: () => { v.arrange(id, "back"); changed(); } });
        items.push({ label: "Delete", separator: true, danger: true, hint: "Del", onClick: () => { v.deleteAnnotation(id); setPicture(null); setSelected(null); } });
        return items;
    }, [countEdit, markDirty]);

    // ---------- Download a copy ----------

    const downloadCopy = useCallback(async () => {
        const viewer = viewerRef.current;
        const current = docRef.current;
        if (!viewer || !current) { setError("Nothing to download — open a PDF first."); return; }
        setHint("Preparing the download…");
        try {
            const exported = new Blob([await viewer.saveAsBlob()], { type: "application/pdf" });
            let out = exported;
            let note = "";
            if (modeRef.current === "edit") {
                // Edit-mode changes are burned in by Drive; when it can't be reached the copy still carries them as annotations.
                try { out = await driveApi.flattenPdf(exported, current.name, tokenRef.current); } catch { note = " Drive couldn't be reached, so the edits are kept as annotations (covers from Edit text are not yet applied)."; }
            }
            const name = current.name.replace(/(\.pdf)?$/i, ".pdf");
            const url = URL.createObjectURL(out);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 10000);
            setHint(`Downloaded ${name}.${note}`);
        } catch (err) {
            setError(`Couldn't prepare the download: ${messageOf(err)}`);
        }
    }, []);

    const selectedId = selected?.id ?? null;
    const getSelectedRect = useCallback(
        () => (selectedId ? viewerRef.current?.annotationClientRect(selectedId) ?? null : null),
        [selectedId]
    );

    const editSelectedText = useCallback(
        (patch: Partial<FreeTextStyle>) => {
            const v = viewerRef.current;
            if (!v || !selected) return;
            suppressUntil.current = Date.now() + 500;
            v.editFreeText(selected.id, patch);
            markDirty();
            countEdit();
            setSelected({ id: selected.id, style: { ...selected.style, ...patch } });
        },
        [countEdit, markDirty, selected]
    );

    const removeSelectedText = useCallback(() => {
        if (selected) viewerRef.current?.deleteAnnotation(selected.id);
        setSelected(null);
    }, [selected]);

    // ---------- Comments (a Drive file only; anyone who can open it) ----------

    const driveId = doc?.drive?.id ?? null;
    const [commentsOpen, setCommentsOpen] = useState(false);
    const commentCount = useOpenCommentCount(driveId);
    const commentsAdapter = useMemo(() => pdfCommentsAdapter(() => viewerRef.current), []);
    const openComments = useCallback(() => {
        if (!docRef.current?.drive) {
            setError("Save this PDF to Drive to comment on it.");
            return;
        }
        setCommentsOpen(true);
    }, []);
    useEffect(() => {
        if (!driveId) setCommentsOpen(false);
    }, [driveId]);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!isCommentsShortcut(e)) return;
            e.preventDefault();
            openComments();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [openComments]);

    // ---------- Open ----------

    const confirmDiscard = useCallback(
        () =>
            statusRef.current !== "dirty" ||
            window.confirm(`Discard your unsaved changes to “${docRef.current?.name ?? "this PDF"}”?`),
        []
    );

    // The desktop app hands over PDFs the person opened from the OS (double-click, "Open with", File > Open).
    useEffect(() => {
        const bridge = (window as unknown as { fg?: { onOpenFile?: (h: (f: { name: string; bytes: Uint8Array }) => void) => () => void } }).fg;
        if (!bridge?.onOpenFile) return;
        return bridge.onOpenFile((f) => {
            void (async () => {
                try {
                    loadedIdRef.current = null;
                    await showInViewer(new Blob([f.bytes as BlobPart], { type: "application/pdf" }));
                    setDoc({ name: f.name, drive: null });
                    setStatus("clean");
                    setError(null);
                    setFileParam(null);
                } catch (err) {
                    setError(messageOf(err));
                }
            })();
        });
    }, [showInViewer, setFileParam]);

    const startOpen = useCallback(() => {
        if (confirmDiscard()) setDialog({ kind: "open-choice" });
    }, [confirmDiscard]);

    const editAfterLoad = useRef(false);
    const enterEditRef = useRef<() => void>(() => undefined);

    const createNew = useCallback(async (c: NewPdfChoice) => {
        const s = PAGE_SIZES.find((x) => x.id === c.size) ?? PAGE_SIZES[0]!;
        const dims: [number, number] = c.landscape ? [s.h, s.w] : [s.w, s.h];
        const bytes = createBlankPdf(Array.from({ length: c.pages }, () => dims), c.name);
        setDialog(null);
        try {
            loadedIdRef.current = null;
            editAfterLoad.current = true;
            await showInViewer(new Blob([bytes as BlobPart], { type: "application/pdf" }));
            setDoc({ name: /\.pdf$/i.test(c.name) ? c.name : `${c.name}.pdf`, drive: null });
            setStatus("clean");
            setError(null);
            setFileParam(null);
        } catch (err) {
            editAfterLoad.current = false;
            setError(messageOf(err));
        }
    }, [showInViewer]);

    const startNew = useCallback(() => {
        if (confirmDiscard()) setDialog({ kind: "new-pdf" });
    }, [confirmDiscard]);

    const onLocalPick: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (!file) return;
        if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
            setError(`${file.name}: only .pdf files can be opened here.`);
            return;
        }
        try {
            loadedIdRef.current = null;
            await showInViewer(file);
            setDoc({ name: file.name, drive: null });
            setStatus("clean");
            setError(null);
            setFileParam(null);
        } catch (err) {
            setError(messageOf(err));
        }
    };

    // ---------- Save ----------

    const runSave = useCallback(
        async (upload: (pdf: Blob) => Promise<DriveItem>) => {
            const viewer = viewerRef.current;
            const current = docRef.current;
            if (!viewer || !current) {
                setError("Nothing to save — open a PDF first.");
                return;
            }
            // Edit mode: the edits are burned into the page before they're saved.
            const burnIn = modeRef.current === "edit";
            statusRef.current = "saving";
            setStatus("saving");
            setError(null);
            changedWhileSaving.current = false;
            let settle = () => {};
            saveInFlightRef.current = new Promise<void>((resolve) => { settle = resolve; });
            try {
                // The document as it is now — annotations, form values, signatures.
                const exported = new Blob([await viewer.saveAsBlob()], { type: "application/pdf" });
                const pdf = burnIn ? await driveApi.flattenPdf(exported, current.name, tokenRef.current) : exported;
                const saved = await upload(pdf);
                loadedIdRef.current = saved.id;
                setDoc({ name: saved.name, drive: saved });
                setFileParam(saved.id);
                const next: SaveStatus = changedWhileSaving.current ? "dirty" : "clean";
                if (burnIn && next === "clean") {
                    // Show what was saved — the edits are part of the page now — and go back to View.
                    await showInViewer(pdf);
                } else {
                    // Still editing (changed during the save): Discard now goes back to the saved version.
                    lastBlobRef.current = pdf;
                }
                // Set the ref now too: Close reads it right after this save settles.
                statusRef.current = next;
                setStatus(next);
            } catch (err) {
                statusRef.current = "failed";
                setStatus("failed");
                setError(`Couldn't save to Drive: ${messageOf(err)}. Your edits are still here — use Download to keep a copy on this computer.`);
            } finally {
                saveInFlightRef.current = null;
                settle();
            }
        },
        [setFileParam, showInViewer]
    );

    /** Save to Drive: a new version when the user may edit the file, otherwise Save As. */
    const save = useCallback(() => {
        const d = docRef.current;
        if (!d) {
            setError("Nothing to save — open a PDF first.");
            return;
        }
        if (statusRef.current === "saving") return;
        const target = d.drive;
        if (target && canSaveInPlace(target)) {
            void runSave((pdf) => driveApi.uploadVersion(target.id, pdf, target.name, tokenRef.current));
        } else {
            setDialog({ kind: "save-as", target: "drive" });
        }
    }, [runSave]);

    // ---------- Properties, events, data links, fonts, fill ----------

    useEffect(() => { viewerRef.current?.setFillWho(who); }, [who, viewerReady, doc]);
    useEffect(() => { void listSecrets().then((l) => setSecretCount(l.length)); }, [dialog?.kind]);
    useEffect(() => { lsSet("fg.pdf.props", propsOpen ? "1" : null); }, [propsOpen]);
    useEffect(() => { lsSet("fg.pdf.autosave", autosave ? "1" : null); }, [autosave]);
    useEffect(() => { lsSet("fg.pdf.unit", unit); }, [unit]);

    const onObjectSelect = useCallback((info: ObjectSelectionInfo | null) => {
        setPropSel((cur) => (info ? { kind: "object", info } : cur.kind === "object" ? { kind: "none" } : cur));
        setBump((b) => b + 1);
    }, []);
    const onLinkSelect = useCallback((l: { num: number; pageIndex: number } | null) => {
        setPropSel(l ? { kind: "link", num: l.num, page: l.pageIndex } : { kind: "none" });
        if (l) { setPropsOpen(true); setPropTab("Properties"); }
        setBump((b) => b + 1);
    }, []);

    const runEnv = useCallback(async (l: DataLink): Promise<RunEnv> => ({
        token: tokenRef.current,
        secret: await secretsSnapshot([l.authRef, l.connectionRef]),
        confirm: (what, url) => window.confirm(`${what}\n${url.slice(0, 120)}?`)
    }), []);

    /** The link that applies to a field (its own, else the form's). */
    const linkFor = useCallback((fieldId: string | null): DataLink | null => {
        const v = viewerRef.current;
        if (!v) return null;
        return (fieldId ? v.fieldExtras(fieldId)?.link ?? null : null) ?? v.formDataLink();
    }, []);

    const runLoad = useCallback(async (fieldId: string | null) => {
        const v = viewerRef.current;
        const link = linkFor(fieldId);
        if (!v) return;
        if (!link) { setHint("There is no data source set. Properties > Data link sets one."); return; }
        try {
            const key = v.formValues().get(link.keyField) ?? "";
            const values = await loadValues(link, key, await runEnv(link));
            const images = new Map(v.imageFields().map((f) => [f.name, f.id]));
            let nt = 0, np = 0;
            const problems: string[] = [];
            for (const [name, val] of values) {
                const img = images.get(name);
                if (img) {
                    try {
                        const bytes = /^https?:\/\//i.test(val) ? await fetchPicture(val, link.cache, []) : Uint8Array.from(atob(val), (c) => c.charCodeAt(0));
                        const bad = await v.setFieldPicture(img, new Blob([bytes as BlobPart], { type: "image/png" }), `${name}.png`);
                        if (bad) problems.push(`${name}: ${bad}`); else np++;
                    } catch (e) { problems.push(`${name}: ${messageOf(e)}`); }
                } else { v.setFormValue(name, val); nt++; }
            }
            setHint(`Loaded ${nt} value${nt === 1 ? "" : "s"} and ${np} picture${np === 1 ? "" : "s"}${problems[0] ? ` (${problems[0]})` : ""}.`);
        } catch (e) { setError(messageOf(e)); }
    }, [linkFor, runEnv]);

    const runSaveData = useCallback(async (fieldId: string | null) => {
        const v = viewerRef.current;
        const link = linkFor(fieldId);
        if (!v) return;
        if (!link) { setHint("There is no data source set. Properties > Data link sets one."); return; }
        try {
            const pairs = await v.submissionPairs();
            const pics: Array<[string, Uint8Array]> = [];
            for (const f of v.imageFields()) { const px = v.fieldPicture(f.id); if (px) pics.push([f.name, await pixelsToPng(px)]); }
            const code = v.fillState().identity?.code ?? v.fillState().pendingCode ?? "";
            setHint(await saveValues(link, pairs, pics, code, await runEnv(link)));
        } catch (e) { setError(messageOf(e)); }
    }, [linkFor, runEnv]);

    const runUpload = useCallback(async (fieldId: string) => {
        const v = viewerRef.current;
        const link = linkFor(fieldId);
        const px = v?.fieldPicture(fieldId);
        if (!v || !link) { setHint("This field has no data link. Properties > Data link sets one."); return; }
        if (!px) { setHint("Add a picture first."); return; }
        try {
            const name = v.imageFields().find((f) => f.id === fieldId)?.name ?? "picture";
            const addr = await uploadPicture(link, name, await pixelsToPng(px), v.formValues().get(link.keyField) ?? "", v.fillState().identity?.code ?? "", await runEnv(link));
            setHint(addr ? `Picture uploaded: ${addr}` : "Picture uploaded.");
        } catch (e) { setError(messageOf(e)); }
    }, [linkFor, runEnv]);

    /** The safe built-in actions of a clicked button, image field or link. Scripts are stored but not run. */
    const onActions = useCallback(async (list: PdfAction[], from: { field: string; widgetId: number }) => {
        const v = viewerRef.current;
        if (!v) return;
        const fieldId = `w:${from.widgetId}`;
        for (const a of list) {
            try {
                switch (a.type) {
                    case "goto": if (!v.goToPage(a.page + 1)) setHint(`There is no page ${a.page + 1}.`); break;
                    case "url": if (safeUri(a.url)) window.open(a.url, "_blank", "noopener"); else setError("That address is not one the Reader opens (only https, http, mailto and tel)."); break;
                    case "reset": v.resetForm(); break;
                    case "showhide": v.showHideField(a.field, a.show); break;
                    case "setvalue": v.setFormValue(a.field, evalSetValue(a.expr, (n) => v.formValues().get(n) ?? "")); break;
                    case "sound": beep(); break;
                    case "print": v.print(); break;
                    case "save": save(); break;
                    case "script": setHint("A script in this form was skipped: scripts are stored and shown but not run here."); break;
                    case "pickimage": v.pickPicture(fieldId); break;
                    case "loaddata": await runLoad(fieldId); break;
                    case "savedata": await runSaveData(fieldId); break;
                    case "upload": await runUpload(fieldId); break;
                    case "submit": {
                        if (!/^https?:\/\//i.test(a.spec.url.trim())) { setError("The submit address has to start with https://"); break; }
                        if (!window.confirm(`Send what is filled in to\n${a.spec.url.slice(0, 120)}?`)) break;
                        const pairs = await v.submissionPairs();
                        const whole = a.spec.format === "PDF" ? await v.saveAsBlob() : undefined;
                        setHint(await sendRequest(buildSubmit(a.spec, pairs, whole)));
                        // links that save when the form is submitted
                        const fl = v.formDataLink();
                        if (fl?.saveOnSubmit) await runSaveData(null);
                        break;
                    }
                }
            } catch (e) { setError(messageOf(e)); }
        }
    }, [runLoad, runSaveData, runUpload, save]); // eslint-disable-line react-hooks/exhaustive-deps

    // links that load when the form opens
    useEffect(() => {
        const v = viewerRef.current;
        if (!doc || !v || !viewerReady || mode === "edit") return;
        const l = v.formDataLink();
        if (l?.loadOnOpen) void runLoad(null);
    }, [doc?.name, viewerReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // AutoSave (off by default): a copy of the open file in Drive is kept up to date while there are unsaved changes; the file itself is untouched
    const autoCopy = useRef<{ key: string; item: DriveItem } | null>(null);
    useEffect(() => {
        if (!autosave || status !== "dirty" || !doc) return;
        const t = window.setTimeout(async () => {
            const v = viewerRef.current;
            if (!v || statusRef.current === "saving") return;
            try {
                const blob = new Blob([await v.saveAsBlob()], { type: "application/pdf" });
                const name = `${stripExtension(doc.name)} (AutoSave).pdf`;
                const key = doc.drive?.id ?? doc.name;
                if (autoCopy.current?.key === key) await driveApi.uploadVersion(autoCopy.current.item.id, blob, name, tokenRef.current);
                else autoCopy.current = { key, item: await driveApi.uploadBlob(blob, name, doc.drive?.parentId ?? null, tokenRef.current) };
                setAutoNote(`AutoSaved ${new Date().toLocaleTimeString()}`);
            } catch (e) { setAutoNote(`AutoSave failed: ${messageOf(e)}`); }
        }, 4000);
        return () => window.clearTimeout(t);
    }, [autosave, status, doc, bump]);

    const goForms = useCallback(() => {
        if (modeRef.current !== "edit") { enterEditRef.current(); window.setTimeout(() => onFormFields(), 60); }
        else onFormFields();
    }, [onFormFields]);

    // ---- the rows of the pane ----

    const pageNow = Math.max(0, (viewerRef.current?.currentPage() || 1) - 1);
    const fillNow = useMemo(() => (viewerReady && doc ? viewerRef.current?.fillState() ?? null : null), [viewerReady, doc, bump]); // eslint-disable-line react-hooks/exhaustive-deps
    const fontsNow = useMemo(() => (viewerReady && doc ? viewerRef.current?.documentFonts() ?? [] : []), [viewerReady, doc, bump]); // eslint-disable-line react-hooks/exhaustive-deps

    const pane = useMemo((): { title: string; rows: PropRow[]; disabled: PropTab[]; current: string | null } => {
        const v = viewerRef.current;
        void bump;
        if (!v || !doc) return { title: "Nothing open", rows: [], disabled: ["Events"], current: null };
        const pc = v.pageCount();
        const evKind = (id: string): "field-text" | "field-choice" | "field-check" | "button" | "image" => {
            const f = v.fieldInfo(id);
            return f?.ftype === "image" ? "image" : f?.ftype === "check" || f?.ftype === "radio" ? "field-check" : f?.ftype === "combo" || f?.ftype === "list" ? "field-choice" : id.startsWith("w:") && !f ? "button" : "field-text";
        };
        const base = { disabled: (propSel.kind === "field" || propSel.kind === "link" ? [] : ["Events"]) as PropTab[] };
        if (propTab === "Page") return { title: `Page ${pageNow + 1}`, rows: pageRows(v.pageInfo(pageNow) ?? { number: pageNow + 1, size: [612, 792], rotate: 0, label: String(pageNow + 1) }, unit), ...base, current: null };
        if (propTab === "Document") return { title: "Document", rows: documentRows({ info: v.docInfo(), pages: pc, fonts: { count: fontsNow.length, loose: fontsNow.filter((f) => f.embedding === "none").length }, fill: fillNow?.identity ?? null, pendingCode: fillNow?.pendingCode ?? null, googleFonts: googleFontsOn(), formLinked: Boolean(v.formDataLink()), secrets: secretCount }), ...base, current: null };
        if (propSel.kind === "link") {
            const li = v.linkInfo(propSel.num, propSel.page);
            if (propTab === "Events") return { title: "Link", rows: li ? eventRows(li.events, triggersFor("link")) : [], ...base, current: null };
            return { title: "Link", rows: li ? linkRows(li, pc) : [], ...base, current: null };
        }
        if (propSel.kind === "field") {
            const f = v.fieldInfo(propSel.id);
            const ex = v.fieldExtras(propSel.id);
            if (!f || !ex) return { title: "Field", rows: [], ...base, current: propSel.id };
            if (propTab === "Events") return { title: f.name, rows: eventRows(ex.events, triggersFor(evKind(propSel.id))), ...base, current: propSel.id };
            const pic = ex.image ? { has: ex.image.hasPicture, source: ex.image.source } : undefined;
            return { title: f.name, rows: fieldRows(f, { page: pageNow + 1, pageCount: pc, hasLink: Boolean(ex.link), picture: pic }), ...base, current: propSel.id };
        }
        if (propSel.kind === "annot") {
            const a = v.annotInfo(propSel.id);
            if (!a) return { title: "Item", rows: [], ...base, current: propSel.id };
            return { title: a.kind, rows: annotRows(a, v.freeTextStyle(propSel.id), v.originalStyle(propSel.id), unit), ...base, current: propSel.id };
        }
        if (propSel.kind === "object") return { title: propSel.info.label, rows: objectRows(propSel.info, unit), ...base, current: `obj:${propSel.info.page}:` };
        return { title: "Nothing selected", rows: [], ...base, current: null };
    }, [bump, propSel, propTab, unit, doc, pageNow, fontsNow, fillNow, secretCount]); // eslint-disable-line react-hooks/exhaustive-deps

    const elements = useMemo(() => (viewerReady && doc ? viewerRef.current?.pageElements(pageNow) ?? [] : []), [viewerReady, doc, pageNow, bump]); // eslint-disable-line react-hooks/exhaustive-deps
    const touch = useCallback(() => { markDirty(); countEdit(); setBump((b) => b + 1); }, [countEdit, markDirty]);

    /** A typed value or a button press in the pane. Returns a sentence when the value is refused. */
    const applyProp = useCallback(async (key: string, value: string): Promise<string | void> => {
        const v = viewerRef.current;
        if (!v) return;
        const done = () => { suppressUntil.current = Date.now() + 400; touch(); };
        try {
            // document and page
            if (propTab === "Document") {
                switch (key) {
                    case "title": case "author": case "subject": case "keywords": v.setDocInfo({ [key]: value }); done(); return;
                    case "fontdl": setGoogleFonts(value === "true"); setBump((b) => b + 1); return;
                    case "btn:fonts": setDialog({ kind: "fonts" }); return;
                    case "btn:verify": setDialog({ kind: "verify" }); return;
                    case "btn:stamp": if (await v.stampFill(pageNow + 1)) { done(); setHint("The fill code was added as a bar code at the bottom left of the page."); } return;
                    case "btn:formlink": setDialog({ kind: "datalink", scope: { type: "form" } }); return;
                    case "btn:secrets": setDialog({ kind: "secrets" }); return;
                }
                return;
            }
            if (propTab === "Page") {
                if (key === "unit") { setUnit(UNITS.find((u) => u.label === value)?.id ?? "pt"); return; }
                if (key === "btn:marks") { setDialog({ kind: "marks" }); return; }
                if (key === "protation") { const cur = v.pageInfo(pageNow)?.rotate ?? 0; const want = parseInt(value, 10) || 0; if (want !== cur) { await v.rotatePage(pageNow, want - cur); done(); } return; }
                return;
            }
            if (propTab === "Events" && key.startsWith("ev:")) {
                const trigger = key.slice(3) as Trigger;
                if (propSel.kind === "field") setDialog({ kind: "events", trigger, target: { type: "field", id: propSel.id } });
                else if (propSel.kind === "link") setDialog({ kind: "events", trigger, target: { type: "link", num: propSel.num, page: propSel.page } });
                return;
            }
            if (key === "unit") { setUnit(UNITS.find((u) => u.label === value)?.id ?? "pt"); return; }

            // page objects
            if (propSel.kind === "object") {
                const info = propSel.info;
                const [x0, y0, x1, y1] = info.box;
                const len = (f: (pt: number) => [number, number, number, number]) => { const pt = parseLen(value, unit); void v.objectOp({ type: "setBox", box: f(pt) }); };
                switch (key) {
                    case "x": len((pt) => [pt, y0, pt + (x1 - x0), y1]); break;
                    case "y": len((pt) => [x0, pt, x1, pt + (y1 - y0)]); break;
                    case "w": len((pt) => [x0, y0, x0 + Math.max(2, pt), y1]); break;
                    case "h": len((pt) => [x0, y0, x1, y0 + Math.max(2, pt)]); break;
                    case "btn:turn_l": await v.objectOp({ type: "turn", deg: -90 }); break;
                    case "btn:turn_r": await v.objectOp({ type: "turn", deg: 90 }); break;
                    case "btn:front": await v.objectOp({ type: "arrange", how: "front" }); break;
                    case "btn:forward": await v.objectOp({ type: "arrange", how: "forward" }); break;
                    case "btn:backward": await v.objectOp({ type: "arrange", how: "backward" }); break;
                    case "btn:back": await v.objectOp({ type: "arrange", how: "back" }); break;
                    case "btn:duplicate": await v.objectOp({ type: "duplicate" }); break;
                    case "btn:delete": await v.objectOp({ type: "delete" }); break;
                    case "btn:same_fill": await v.objectOp({ type: "sameColour" }); return;
                    case "btn:edit": v.editObjectText(); return;
                    default: if (key.startsWith("btn:align_")) await v.objectOp({ type: "align", how: key.slice(10) as "left" }); else return;
                }
                done();
                return;
            }

            // added annotations
            if (propSel.kind === "annot") {
                const id = propSel.id;
                const a = v.annotInfo(id);
                if (!a) return;
                const box = a.box;
                const setBox = (b: [number, number, number, number]) => v.setAnnotBox(id, b);
                switch (key) {
                    case "x": setBox([parseLen(value, unit), box[1], box[2], box[3]]); break;
                    case "y": setBox([box[0], parseLen(value, unit), box[2], box[3]]); break;
                    case "w": setBox([box[0], box[1], Math.max(4, parseLen(value, unit)), box[3]]); break;
                    case "h": setBox([box[0], box[1], box[2], Math.max(4, parseLen(value, unit))]); break;
                    case "font": v.editFreeText(id, { fontFamily: value }); break;
                    case "size": { const n = Number(value); if (!(n >= 1 && n <= 400)) return "Size has to be between 1 and 400."; v.editFreeText(id, { fontSize: n }); break; }
                    case "bold": v.editFreeText(id, { bold: value === "true" }); break;
                    case "italic": v.editFreeText(id, { italic: value === "true" }); break;
                    case "color": { const c = parseColor(value); if (!c) return "Type a colour like #2563EB"; v.editFreeText(id, { fontColor: c }); break; }
                    case "align": v.editFreeText(id, { align: value === "Centre" ? "center" : value === "Right" ? "right" : "left" }); break;
                    case "leading": { const n = Number(value); if (!(n >= 0.6 && n <= 5)) return "Line spacing has to be between 0.6 and 5."; v.editFreeText(id, { lineSpacing: n }); break; }
                    case "text": v.patchAnnot(id, { text: value }); break;
                    case "fill": v.patchAnnot(id, { fill: parseColor(value) }); break;
                    case "stroke": v.patchAnnot(id, { stroke: parseColor(value) ?? "" }); break;
                    case "width": { const n = Number(value); if (!(n >= 0)) return "Line width has to be a number."; v.patchAnnot(id, { width: n }); break; }
                    case "opacity": { const n = Number(value); if (!(n >= 0 && n <= 100)) return "Opacity is a percentage from 0 to 100."; v.patchAnnot(id, { opacity: n / 100 }); break; }
                    case "rotation": v.editImage(id, { rotation: Number(value) || 0 }); break;
                    case "btn:front": v.arrange(id, "front"); break;
                    case "btn:back": v.arrange(id, "back"); break;
                    case "btn:duplicate": v.duplicateAnnotation(id); break;
                    case "btn:delete": v.deleteAnnotation(id); setPropSel({ kind: "none" }); break;
                    case "btn:edit": v.startEditing(id); return;
                    case "btn:replace": setPicture({ id, look: { opacity: a.opacity ?? 1, rotation: a.rotation ?? 0 } }); replaceInputRef.current?.click(); return;
                    case "btn:match": { await v.matchOriginal(id); break; }
                    default: return;
                }
                const st = v.freeTextStyle(id);
                if (selected && st) setSelected({ id, style: st });
                done();
                return;
            }

            // links
            if (propSel.kind === "link") {
                if (key === "link_type") { const li = v.linkInfo(propSel.num, propSel.page); if (value === "A web address") v.patchLink(propSel.num, { uri: li?.uri ?? "https://" }); else v.patchLink(propSel.num, { page: li?.goesTo ?? 0 }); }
                else if (key === "link_url") { if (!safeUri(value)) return "An address has to start with https://, http://, mailto: or tel:"; v.patchLink(propSel.num, { uri: value }); }
                else if (key === "link_page") { const n = Math.round(Number(value)); if (!(n >= 1 && n <= v.pageCount())) return `The document has ${v.pageCount()} pages.`; v.patchLink(propSel.num, { page: n - 1 }); }
                else return;
                done();
                return;
            }

            // form fields
            if (propSel.kind === "field") {
                const id = propSel.id;
                const f = v.fieldInfo(id);
                if (!f) return;
                const ex = v.fieldExtras(id);
                const patch = (p: Partial<FieldInfo>) => { const next = v.patchField(id, p); if (next) setFieldSel(next); };
                const img = (p: Partial<NonNullable<FieldInfo["image"]>>) => { if (f.image) patch({ image: { ...f.image, ...p } }); };
                switch (key) {
                    case "name": { if (!value.trim()) return "A field needs a name."; if (v.fieldNames().includes(value) && value !== f.name) return "Another field already has that name."; patch({ name: value.replace(/[^\w.-]/g, "_") }); break; }
                    case "tooltip": patch({ label: value }); break;
                    case "required": patch({ required: value === "true" }); break;
                    case "readonly": patch({ readOnly: value === "true" }); break;
                    case "maxlen": patch({ maxLen: Math.max(0, Math.round(Number(value) || 0)) }); break;
                    case "multiline": patch({ multiline: value === "true" }); break;
                    case "password": patch({ password: value === "true" }); break;
                    case "comb": patch({ comb: value === "true", ...(value === "true" && f.maxLen === 0 ? { maxLen: 10 } : {}) }); break;
                    case "default": patch({ defaultValue: value }); break;
                    case "export": patch({ exportValue: value }); break;
                    case "options": patch({ options: value.split("\n").map((x) => x.trim()).filter(Boolean) }); break;
                    case "multisel": patch({ multiSelect: value === "true" }); break;
                    case "vtype": patch({ validation: { ...f.validation, type: (VALIDATION_CHOICES.find((c) => c.label === value)?.type ?? "none") as FieldInfo["validation"]["type"] } }); break;
                    case "vmin": patch({ validation: { ...f.validation, min: value.trim() === "" ? undefined : Number(value) } }); break;
                    case "vmax": patch({ validation: { ...f.validation, max: value.trim() === "" ? undefined : Number(value) } }); break;
                    case "vpattern": patch({ validation: { ...f.validation, pattern: value } }); break;
                    case "vmessage": patch({ validation: { ...f.validation, message: value } }); break;
                    case "fill": patch({ style: { ...f.style, fill: parseColor(value) ?? undefined } }); break;
                    case "border": patch({ style: { ...f.style, stroke: parseColor(value) ?? undefined } }); break;
                    case "bwidth": patch({ style: { ...f.style, borderWidth: Math.max(0, Number(value) || 0) } }); break;
                    case "img_fit": img({ fit: value === "Fill the box" ? "fill" : value === "Stretch" ? "stretch" : "fit" }); break;
                    case "img_shape": img({ shape: value === "Circle" ? "circle" : value === "Rounded corners" ? "rounded" : "square" }); break;
                    case "img_placeholder": img({ placeholder: value }); break;
                    case "img_maxbytes": img({ maxBytes: Math.max(0, Math.round((Number(value) || 0) * 1024)) }); break;
                    case "img_types": img({ types: value.split(",").map((x) => x.trim().replace(/^\./, "").toLowerCase()).filter(Boolean) }); break;
                    case "img_maxpx": img({ maxPx: Math.max(0, Math.round(Number(value) || 0)) }); break;
                    case "btn:pick": v.pickPicture(id); return;
                    case "btn:crop": setDialog({ kind: "crop", id }); return;
                    case "btn:cutout": { const px = v.fieldPicture(id); if (!px) return "There is no picture to cut out."; const bad = await v.setFieldPicture(id, new Blob([await pixelsToPng(cutOutBackdrop(px)) as BlobPart], { type: "image/png" }), ex?.image?.source || "cutout.png"); if (bad) return bad; break; }
                    case "btn:removepic": { await v.setFieldPicture(id, null); break; }
                    case "btn:link": setDialog({ kind: "datalink", scope: { type: "field", id } }); return;
                    case "btn:delete": v.deleteAnnotation(id); setFieldSel(null); setPropSel({ kind: "none" }); break;
                    default: return;
                }
                done();
            }
        } catch (e) { return messageOf(e); }
    }, [propTab, propSel, unit, pageNow, selected, touch]); // eslint-disable-line react-hooks/exhaustive-deps

    const resetProp = useCallback((key: string) => {
        const row = pane.rows.find((r) => r.key === key);
        if (row?.def !== undefined) void applyProp(key, row.def);
    }, [pane, applyProp]);

    const pickElement = useCallback((id: string) => { if (id) viewerRef.current?.selectElement(id); }, []);

    const onSaveAs = async (choice: SaveAsChoice) => {
        setDialog(null);
        const fileName = `${choice.name}.pdf`;
        if (choice.target === "computer") {
            try {
                const viewer = viewerRef.current;
                if (!viewer) throw new Error("Open a PDF first.");
                const exported = new Blob([await viewer.saveAsBlob()], { type: "application/pdf" });
                const pdf = modeRef.current === "edit"
                    ? await driveApi.flattenPdf(exported, fileName, tokenRef.current)
                    : exported;
                saveBlobToComputer(pdf, fileName);
            } catch (err) {
                setError(`Couldn't download: ${messageOf(err)}`);
            }
            return;
        }
        await runSave((pdf) => driveApi.uploadBlob(pdf, fileName, choice.folderId, tokenRef.current));
    };

    // ---------- View / Edit ----------

    const enterEdit = useCallback(() => {
        const v = viewerRef.current;
        if (modeRef.current === "edit" || !v || !docRef.current) return;
        modeRef.current = "edit";
        setMode("edit");
        editsRef.current = 0;
        setEdits(0);
        pairsRef.current.clear();
        setSelected(null);
        v.setEditLook(true);
        setHint("Click any text to edit it, or click a picture to move or remove it. The tools add more. Your edits are saved into the PDF when you save to Drive.");
    }, []);

    enterEditRef.current = enterEdit;

    /** Reloads the version last opened or saved — every unsaved change goes. */
    const revert = useCallback(async () => {
        const blob = lastBlobRef.current;
        if (!blob) {
            exitEditState();
            return;
        }
        try {
            await showInViewer(blob);
            changedWhileSaving.current = false;
            statusRef.current = "clean";
            setStatus("clean");
        } catch (err) {
            setError(`Couldn't discard the edits: ${messageOf(err)}`);
        }
    }, [exitEditState, showInViewer]);

    /** False when the user chose to keep editing. */
    const leaveEdit = useCallback(async (): Promise<boolean> => {
        if (modeRef.current !== "edit") return true;
        const n = editsRef.current;
        if (n === 0) {
            exitEditState();
            return true;
        }
        if (!window.confirm(`Discard ${plural(n, "edit")} and go back to View? Edits are only kept when you save them to Drive.`)) {
            return false;
        }
        await revert();
        return true;
    }, [exitEditState, revert]);

    const discardEdits = useCallback(async () => {
        const n = editsRef.current;
        if (n === 0) {
            exitEditState();
            return;
        }
        if (!window.confirm(`Discard ${plural(n, "edit")}? The PDF goes back to how it was when you opened or last saved it.`)) return;
        await revert();
    }, [exitEditState, revert]);

    const onModeChange = useCallback(
        (next: PdfMode) => {
            if (next === "edit") enterEdit();
            else void leaveEdit();
        },
        [enterEdit, leaveEdit]
    );

    // Ctrl+E switches between Read and Edit; F4 opens and closes Properties
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "F4" && !e.ctrlKey && !e.altKey && !e.metaKey) { e.preventDefault(); setPropsOpen((o) => !o); return; }
            if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "e") {
                e.preventDefault();
                if (!docRef.current) return;
                if (modeRef.current === "edit") void leaveEdit(); else if (canEditRef.current) enterEditRef.current();
            }
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [leaveEdit]);

    // Esc lets go of the tool being placed.
    useEffect(() => {
        if (mode !== "edit") return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || toolRef.current === "none") return;
            viewerRef.current?.setTool("None");
            setTool("none");
            setHint(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [mode, setTool]);

    // ---------- Close ----------

    // PDF has no separate home: Close unloads the document and drops
    // ?fileId= / ?openFile= (replace), which brings back the "Open a PDF"
    // start screen. A save in flight finishes first — never cut off; changes
    // still unsaved after it (or a failed save) ask first.
    const [closing, setClosing] = useState(false);
    const closingRef = useRef(false);
    const closeFile = useCallback(async () => {
        if (closingRef.current) return;
        closingRef.current = true;
        setClosing(true);
        try {
            if (saveInFlightRef.current) await saveInFlightRef.current;
            const s = statusRef.current;
            if (
                docRef.current &&
                (s === "dirty" || s === "failed" || s === "saving") &&
                !window.confirm(`You have unsaved changes to “${docRef.current.name}”. Close without saving?`)
            ) {
                return;
            }
            openSeqRef.current += 1; // an open still in progress drops its result
            loadedIdRef.current = null;
            urlOpened.current = true;
            docRef.current = null;
            ignoreChangesUntil.current = Date.now() + 1000;
            exitEditState();
            viewerRef.current?.unload();
            lastBlobRef.current = null;
            changedWhileSaving.current = false;
            statusRef.current = "clean";
            setDoc(null);
            setStatus("clean");
            setLoading(false);
            setError(null);
            setDialog(null);
            setCommentsOpen(false);
            setFileParam(null);
        } finally {
            closingRef.current = false;
            setClosing(false);
        }
    }, [exitEditState, setFileParam]);

    // Ctrl+S saves, Ctrl+Shift+S is Save as — ahead of the viewer's own keys.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key.toLowerCase() !== "s" || !(e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            e.stopPropagation();
            if (!docRef.current) return;
            if (e.shiftKey) setDialog({ kind: "save-as", target: "drive" });
            else save();
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [save]);

    // Leaving with unsaved changes asks first.
    useEffect(() => {
        if (status !== "dirty" && status !== "saving") return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [status]);

    // The viewer's own Open / Download toolbar buttons use our dialogs.
    const onViewerToolbarClick = useCallback(
        (args: unknown) => {
            try {
                const a = args as { item?: { id?: string; tooltipText?: string }; cancel?: boolean };
                const id = (a?.item?.id ?? "").toLowerCase();
                const tip = (a?.item?.tooltipText ?? "").toLowerCase();
                if (id.includes("open") || tip === "open") {
                    a.cancel = true;
                    startOpen();
                    return;
                }
                if (id.includes("download") || tip === "download") {
                    a.cancel = true;
                    if (docRef.current) setDialog({ kind: "save-as", target: "computer" });
                }
            } catch {
                /* swallow */
            }
        },
        [startOpen]
    );

    const drive = doc?.drive ?? null;
    const inPlace = drive ? canSaveInPlace(drive) : false;
    const startFolderId = drive && !drive.ownerEmail ? drive.parentId : null;
    // Something to close: a document, one opening, or a failed ?fileId= open.
    const canClose = Boolean(doc || loading || fileIdParam || openFileUrl);
    const saving = status === "saving";
    // A view-only shared file can't be edited — Save as makes an own copy first.
    const canEdit = Boolean(doc) && !loading && !saving && (!drive || inPlace);
    canEditRef.current = canEdit;
    const editTitle = !doc
        ? "Open a PDF to edit it"
        : drive && !inPlace
            ? `View only — ${drive.ownerEmail ?? "the owner"} shared this with view access. Save as… to edit your own copy.`
            : "Edit text, add images, shapes, signatures and form fields, and organise pages";

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" hidden onChange={onLocalPick} />
            <input ref={imageInputRef} type="file" accept="image/png,image/jpeg" hidden onChange={onImagePicked} />

            {/* File name + save status live in the suite top bar's title region. */}
            <ShellHeaderPortal>
                <span className="min-w-0 truncate text-sm font-medium" title={doc?.name}>
                    {doc?.name ?? (loading ? "Opening…" : "No PDF open")}
                </span>
                <span aria-live="polite" className="hidden shrink-0 items-center sm:flex">
                    <StatusBadge status={status} loading={loading} doc={doc} inPlace={inPlace} onRetry={save} />
                </span>
                {doc ? (
                    <span className="hidden shrink-0 items-center gap-1.5 sm:flex" data-header-pills>
                        {mode === "edit" ? <Pill label="Editing" on tone="edit" icon={<PencilLine className="h-3 w-3" aria-hidden />} title="You are editing. Click or press Ctrl+E to go back to reading." onClick={() => void leaveEdit()} /> : null}
                        <Pill label={`AutoSave ${autosave ? "on" : "off"}`} on={autosave} title={autosave ? `AutoSave keeps a copy named “${stripExtension(doc.name)} (AutoSave).pdf” in Drive up to date while there are unsaved changes. ${autoNote ?? ""}` : "AutoSave is off. Turn it on to keep a copy of this file in Drive up to date while you work; the file itself is only changed when you save."} onClick={() => { setAutosave((a) => !a); setAutoNote(null); }} />
                        {fillNow && (fillNow.identity || fillNow.pendingCode) ? <Pill label={`Fill ${(fillNow.identity?.code ?? fillNow.pendingCode)!}`} on={Boolean(fillNow.identity?.fingerprint)} title="The fill ID of this copy. Click to verify its fingerprint." onClick={() => setDialog({ kind: "verify" })} /> : null}
                        <Pill label="Properties" on={propsOpen} icon={<PanelRight className="h-3 w-3" aria-hidden />} title="Properties (F4)" onClick={() => setPropsOpen((o) => !o)} />
                    </span>
                ) : null}
                {/* Close — the last editor control, as in Docs / Sheets / Slides. */}
                {canClose ? (
                    <div className="suite-appbar-divider ml-auto flex shrink-0 items-center pl-1.5">
                        <button
                            type="button"
                            onClick={() => void closeFile()}
                            disabled={closing}
                            aria-label="Close file"
                            aria-busy={closing || undefined}
                            title={closing ? "Finishing the save before closing…" : "Close file (return to PDF home)"}
                            className="suite-appbar-btn flex h-8 w-8 items-center justify-center rounded disabled:cursor-wait"
                        >
                            {closing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
                        </button>
                    </div>
                ) : null}
            </ShellHeaderPortal>

            {/* File bar: View / Edit, then Open / Save to Drive / Save as / Comments. */}
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b bg-background px-3 py-1.5">
                <ModeSwitch
                    mode={mode}
                    onChange={onModeChange}
                    editDisabled={!canEdit}
                    viewDisabled={saving}
                    editTitle={editTitle}
                    forms={mode === "edit" && formOpen}
                    onForms={goForms}
                />
                <span className="hidden h-5 w-px bg-border sm:block" aria-hidden />
                <div className="flex flex-wrap items-center gap-1">
                    <BarButton icon={FilePlus2} onClick={startNew} title="Create a new, blank PDF to build from scratch">
                        New
                    </BarButton>
                    <BarButton icon={ShieldCheck} onClick={() => setDialog({ kind: "digital-sign" })} disabled={!doc || saving} title="Sign this PDF with a certificate, or check the signatures in it">
                        Digital signature
                    </BarButton>
                    <BarButton icon={ScanText} onClick={() => setDialog({ kind: "ocr" })} disabled={!doc || saving} title="Make scanned pages searchable by recognising their text">
                        Recognise text
                    </BarButton>
                    <BarButton icon={GitCompare} onClick={() => setDialog({ kind: "compare" })} disabled={!doc} title="Compare the text of this PDF with another one">
                        Compare
                    </BarButton>
                    <BarButton icon={FolderOpen} onClick={startOpen} title="Open a PDF from Drive or this computer">
                        Open
                    </BarButton>
                    <BarButton
                        icon={Save}
                        primary={mode === "view"}
                        onClick={save}
                        disabled={!doc || saving}
                        title={
                            mode === "edit"
                                ? "Burn the edits into the PDF and save it to Drive (Ctrl+S)"
                                : inPlace ? "Save a new version to Drive (Ctrl+S)" : "Save a copy to Drive (Ctrl+S)"
                        }
                    >
                        Save to Drive
                    </BarButton>
                    <BarButton
                        icon={CopyPlus}
                        onClick={() => setDialog({ kind: "save-as", target: "drive" })}
                        disabled={!doc || saving}
                        title="Save as a new file (Ctrl+Shift+S)"
                    >
                        Save as…
                    </BarButton>
                    <CommentsButton
                        file={drive}
                        tone="surface"
                        openCount={commentCount.count}
                        active={commentsOpen}
                        onClick={commentsOpen ? () => setCommentsOpen(false) : openComments}
                    />
                    {drive ? (
                        <a
                            href={`${DRIVE_WEB_URL.replace(/\/$/, "")}/open/${encodeURIComponent(drive.id)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Show this file in Drive"
                            className="hidden h-8 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground md:flex"
                        >
                            <ExternalLink className="h-4 w-4" aria-hidden /> Drive
                        </a>
                    ) : null}
                    <BarButton
                        icon={X}
                        onClick={() => void closeFile()}
                        disabled={!canClose || closing}
                        title="Close this PDF (return to the start screen)"
                    >
                        Close
                    </BarButton>
                </div>
                {mode === "edit" ? (
                    <span className="pdf-edit-text ml-auto flex shrink-0 items-center gap-1.5 text-xs font-semibold" aria-live="polite">
                        <PencilLine className="h-3.5 w-3.5" aria-hidden />
                        {plural(edits, "edit")}
                    </span>
                ) : null}
            </div>

            {mode === "edit" ? (
                <EditToolbar
                    tool={tool}
                    formOpen={formOpen}
                    edits={edits}
                    saving={saving}
                    inPlace={inPlace}
                    hint={hint}
                    onEditText={onEditText}
                    onAddText={onAddText}
                    onNote={onNote}
                    canUndo={history.canUndo}
                    canRedo={history.canRedo}
                    onUndo={() => viewerRef.current?.undo()}
                    onRedo={() => viewerRef.current?.redo()}
                    onSpace={onSpace}
                    onTable={onTable}
                    onPageMarks={() => setDialog({ kind: "marks" })}
                    redactCount={redactCount}
                    onRedact={onRedact}
                    onApplyRedactions={() => setDialog({ kind: "redact" })}
                    onImage={() => imageInputRef.current?.click()}
                    onShape={onShape}
                    onSign={onSign}
                    onFormFields={onFormFields}
                    onPages={onPages}
                    onDiscard={() => void discardEdits()}
                    onSave={save}
                    onDownload={() => void downloadCopy()}
                />
            ) : null}

            {error ? (
                <div
                    role="alert"
                    className="flex shrink-0 items-start gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                >
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span className="flex-1">{error}</span>
                    {!doc && fileIdParam && !loading ? (
                        <button
                            type="button"
                            onClick={() => void openFromDrive(fileIdParam)}
                            className="shrink-0 font-medium underline-offset-2 hover:underline"
                        >
                            Try again
                        </button>
                    ) : null}
                    <button type="button" onClick={() => setError(null)} aria-label="Dismiss" className="shrink-0 rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            <div className="flex min-h-0 flex-1">
            <div
                className="relative min-h-0 min-w-0 flex-1"
                onDragOver={(e) => { if (formOpen && e.dataTransfer.types.includes("application/x-fg-field")) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; } }}
                onDrop={(e) => {
                    const kind = e.dataTransfer.getData("application/x-fg-field") as PaletteKind;
                    if (!formOpen || !kind) return;
                    e.preventDefault();
                    if (viewerRef.current?.dropField(kind, e.clientX, e.clientY)) { markDirty(); countEdit(); setArmed(null); }
                }}
            >
                <Suspense
                    fallback={
                        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading viewer…
                        </div>
                    }
                >
                    <PdfViewerHost
                        ref={viewerRef}
                        onReady={onViewerReady}
                        onToolbarClick={onViewerToolbarClick}
                        onDocumentLoad={onDocumentLoad}
                        onDocumentLoadFailed={onDocumentLoadFailed}
                        onDocumentChange={onDocumentChange}
                        onAnnotationSelect={onAnnotationSelect}
                        onAnnotationContextMenu={onAnnotationContextMenu}
                        onEditText={onEditPageText}
                        onHistory={setHistory}
                        onPagesClosed={onPagesClosed}
                        onActions={(list, from) => void onActions(list, from)}
                        onObjectSelect={onObjectSelect}
                        onLinkSelect={onLinkSelect}
                    />
                </Suspense>
                {mode === "edit" && formOpen ? (
                <FormBuilder
                    style={formStyle}
                    onStyle={(s, all) => { setFormStyleState(s); viewerRef.current?.setFormStyle(s, all); if (all) { markDirty(); countEdit(); } }}
                    armed={armed}
                    onArm={(k) => { viewerRef.current?.armField(k); setArmed(k); setHint(k ? "Click on the page where it should go." : null); }}
                    field={fieldSel}
                    onPatch={(patch) => { const v = viewerRef.current; if (!v || !fieldSel) return; const next = v.patchField(fieldSel.id, patch); if (next) { setFieldSel(next); markDirty(); countEdit(); } }}
                    onAddOption={() => { if (fieldSel) { viewerRef.current?.addOption(fieldSel.id); markDirty(); countEdit(); } }}
                    onDelete={() => { if (fieldSel) { viewerRef.current?.deleteAnnotation(fieldSel.id); setFieldSel(null); markDirty(); countEdit(); } }}
                    onClose={onFormFields}
                />
            ) : null}
                {viewerReady && !doc && !loading && !fileIdParam && !openFileUrl ? (
                    <div className="pointer-events-none absolute inset-x-0 top-24 flex justify-center px-4">
                        <div className="pointer-events-auto w-full max-w-sm rounded-xl border bg-card p-6 text-center shadow-lg">
                            <FileType2 className="mx-auto h-10 w-10 text-red-600 dark:text-red-400" aria-hidden />
                            <h2 className="mt-3 text-base font-semibold">Open a PDF</h2>
                            <p className="mt-1 text-sm text-muted-foreground">View, annotate, fill in, sign and edit — then save it back to Drive.</p>
                            <div className="mt-4 flex flex-wrap justify-center gap-2">
                                <BarButton icon={FilePlus2} primary onClick={startNew}>New blank PDF</BarButton>
                                <BarButton icon={HardDrive} onClick={() => setDialog({ kind: "drive-picker" })}>From Drive</BarButton>
                                <BarButton icon={Monitor} onClick={() => fileInputRef.current?.click()}>From this computer</BarButton>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
            {propsOpen && doc ? (
                <PropertiesPane
                    title={pane.title}
                    elements={elements}
                    currentElement={pane.current}
                    onPickElement={pickElement}
                    tab={propTab}
                    onTab={setPropTab}
                    disabledTabs={pane.disabled}
                    rows={pane.rows}
                    onApply={applyProp}
                    onReset={resetProp}
                    onClose={() => setPropsOpen(false)}
                    width={propsW}
                    onWidth={(w) => { setPropsW(w); lsSet("fg.pdf.props.w", String(w)); }}
                />
            ) : null}
            </div>

            <input ref={replaceInputRef} type="file" accept="image/png,image/jpeg" hidden onChange={onReplacePicked} />
            {menu ? <ContextMenu x={menu.x} y={menu.y} items={menuItems(menu.id)} onClose={() => setMenu(null)} /> : null}
            {mode === "edit" && picture && !selected ? (
                <ImageMiniToolbar
                    key={picture.id}
                    getRect={getPictureRect}
                    look={picture.look}
                    onChange={editPicture}
                    onReplace={() => replaceInputRef.current?.click()}
                    onArrange={arrangePicture}
                    onRemove={removePicture}
                />
            ) : null}

            {mode === "edit" && selected ? (
                <FreeTextMiniToolbar
                    key={selected.id}
                    getRect={getSelectedRect}
                    style={selected.style}
                    onChange={editSelectedText}
                    onRemove={removeSelectedText}
                    original={viewerRef.current?.originalStyle(selected.id) ?? null}
                    onMatch={() => { void viewerRef.current?.matchOriginal(selected.id).then(() => { const st = viewerRef.current?.freeTextStyle(selected.id); if (st) setSelected({ id: selected.id, style: st }); markDirty(); setBump((b) => b + 1); }); }}
                />
            ) : null}

            {commentsOpen && drive ? (
                <CommentsPanel
                    key={drive.id}
                    file={{ id: drive.id, name: drive.name }}
                    adapter={commentsAdapter}
                    onClose={() => setCommentsOpen(false)}
                    onOpenCountChange={commentCount.set}
                />
            ) : null}

            {dialog?.kind === "digital-sign" ? <DigitalSignDialog pageCount={viewerRef.current?.pageCount() ?? 0} read={() => viewerRef.current?.readSignatures() ?? Promise.resolve([])} onClose={() => setDialog(null)} onSign={async (id, o) => {
                pagesPendingRef.current = true;
                ignoreChangesUntil.current = 0;
                try { await viewerRef.current!.signDocument(id, o); setHint("Signed. Save to Drive to keep the signed file; changing it afterwards invalidates the signature."); } catch (e) { pagesPendingRef.current = false; throw e; }
            }} /> : null}
            {dialog?.kind === "ocr" ? <OcrDialog pageCount={viewerRef.current?.pageCount() ?? 0} onClose={() => setDialog(null)} onRun={async (o) => {
                pagesPendingRef.current = true;
                ignoreChangesUntil.current = 0;
                try { const n = await viewerRef.current!.runOcr(o); if (!n) pagesPendingRef.current = false; else setHint(`Text recognised on ${n} page${n === 1 ? "" : "s"}. Save to Drive to keep it.`); return n; } catch (e) { pagesPendingRef.current = false; throw e; }
            }} /> : null}
            {dialog?.kind === "compare" ? <CompareDialog current={lastBlobRef.current} currentName={doc?.name ?? "this PDF"} onGoTo={(p) => { setDialog(null); viewerRef.current?.goToPage(p); }} onClose={() => setDialog(null)} /> : null}
            {dialog?.kind === "marks" ? <PageMarksDialog pageCount={viewerRef.current?.pageCount() ?? 0} fileName={doc?.name ?? "document.pdf"} onClose={() => setDialog(null)} onApply={(m) => void applyMarks(m)} /> : null}
            {dialog?.kind === "redact" ? <RedactDialog count={redactCount} onClose={() => setDialog(null)} onApply={(s) => void applyRedactions(s)} /> : null}
            {dialog?.kind === "table-size" ? (
                <TableSizeDialog onClose={() => setDialog(null)} onCreate={(rows, cols) => {
                    setDialog(null);
                    viewerRef.current?.armTable(rows, cols);
                    setTool("table");
                    setHint(`Click on the page where the ${rows} × ${cols} table should go. Double-click a cell to type in it.`);
                }} />
            ) : null}
            {dialog?.kind === "new-pdf" ? <NewPdfDialog onClose={() => setDialog(null)} onCreate={(c) => void createNew(c)} /> : null}
            {dialog?.kind === "fonts" ? <DocumentFontsDialog fonts={viewerRef.current?.documentFonts() ?? []} onReplace={async (o, n) => { const c = await viewerRef.current!.replaceFont(o, n); markDirty(); countEdit(); setBump((b) => b + 1); return c; }} onClose={() => setDialog(null)} /> : null}
            {dialog?.kind === "verify" ? <VerifyFillDialog verify={(k) => viewerRef.current!.verifyFill(k)} onClose={() => setDialog(null)} /> : null}
            {dialog?.kind === "secrets" ? <SecretsDialog onClose={() => setDialog(null)} onChange={() => void listSecrets().then((l) => setSecretCount(l.length))} /> : null}
            {dialog?.kind === "events" ? (() => {
                const v = viewerRef.current;
                const t = dialog.target;
                const events: EventMap = t.type === "field" ? v?.fieldExtras(t.id)?.events ?? {} : v?.linkInfo(t.num, t.page)?.events ?? {};
                const label = TRIGGERS.find((x) => x.id === dialog.trigger)?.label ?? "Event";
                return (
                    <ActionsEditor
                        title={`${label} (${t.type === "field" ? v?.fieldInfo(t.id)?.name ?? "field" : "link"})`}
                        list={events[dialog.trigger] ?? []}
                        pageCount={v?.pageCount() ?? 0}
                        fields={v?.fieldNames() ?? []}
                        onClose={() => setDialog(null)}
                        onSave={(list) => {
                            const next = { ...events };
                            if (list.length) next[dialog.trigger] = list; else delete next[dialog.trigger];
                            if (t.type === "field") v?.patchFieldExtras(t.id, { events: next }); else v?.patchLink(t.num, { events: next });
                            setDialog(null);
                            touch();
                        }}
                    />
                );
            })() : null}
            {dialog?.kind === "datalink" ? (() => {
                const v = viewerRef.current;
                const sc = dialog.scope;
                const cur = sc.type === "form" ? v?.formDataLink() ?? null : v?.fieldExtras(sc.id)?.link ?? null;
                return (
                    <DataLinkDialog
                        title={sc.type === "form" ? "this form" : v?.fieldInfo(sc.id)?.name ?? "field"}
                        link={cur}
                        fields={v?.fieldNames() ?? []}
                        onClose={() => setDialog(null)}
                        onSecrets={() => setDialog({ kind: "secrets" })}
                        onTest={async (l) => testLink(l, await runEnv(l))}
                        onSave={(l) => { if (sc.type === "form") v?.setFormDataLink(l); else v?.patchFieldExtras(sc.id, { link: l }); setDialog(null); touch(); }}
                    />
                );
            })() : null}
            {dialog?.kind === "crop" ? (() => {
                const v = viewerRef.current;
                const id = dialog.id;
                const px = v?.fieldPicture(id);
                if (!v || !px) return null;
                return <CropPictureDialog pixels={px} onClose={() => setDialog(null)} onApply={async (c) => {
                    const cut = cropPixels(px, c);
                    if (!cut) return "The crop is too small.";
                    const bad = await v.setFieldPicture(id, new Blob([await pixelsToPng(cut) as BlobPart], { type: "image/png" }), v.fieldExtras(id)?.image?.source || "cropped.png");
                    if (bad) return bad;
                    setDialog(null);
                    touch();
                    return undefined;
                }} />;
            })() : null}
            {dialog?.kind === "open-choice" ? (
                <OpenChoiceDialog
                    onClose={() => setDialog(null)}
                    onPickComputer={() => {
                        setDialog(null);
                        fileInputRef.current?.click();
                    }}
                    onPickDrive={() => setDialog({ kind: "drive-picker" })}
                />
            ) : null}
            {dialog?.kind === "drive-picker" ? (
                <DrivePickerDialog
                    onClose={() => setDialog(null)}
                    onPick={(item) => {
                        setDialog(null);
                        void openFromDrive(item.id);
                    }}
                />
            ) : null}
            {dialog?.kind === "save-as" ? (
                <SaveAsDialog
                    defaultName={stripExtension(doc?.name ?? "Untitled")}
                    initialTarget={dialog.target}
                    startFolderId={startFolderId}
                    onClose={() => setDialog(null)}
                    onConfirm={(choice) => void onSaveAs(choice)}
                />
            ) : null}
        </div>
    );
}

/** A small rounded status button in the top bar: Editing, AutoSave, Fill, Properties. */
function Pill({ label, on, tone, icon, title, onClick }: { label: string; on?: boolean; tone?: "edit"; icon?: ReactNode; title: string; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} title={title} aria-pressed={on} data-pill={label.split(" ")[0]} className={cn("suite-appbar-btn flex h-6 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 text-[11px] font-medium", on ? "border-current opacity-100" : "border-current/40 opacity-70", tone === "edit" && "bg-red-600/15")}>
            {icon}{label}
        </button>
    );
}

/** Crop a picture of an image field by percentages from each side, with a live preview. */
function CropPictureDialog({ pixels, onApply, onClose }: { pixels: import("@fg/web-core/pdf").Pixels; onApply(c: [number, number, number, number]): Promise<string | void>; onClose(): void }) {
    const [m, setM] = useState({ l: 0, t: 0, r: 0, b: 0 });
    const [err, setErr] = useState<string | null>(null);
    const url = pixelsToUrl(pixels);
    const num = (k: "l" | "t" | "r" | "b", label: string) => <label className="block text-xs">{label} (%)<input type="number" min={0} max={90} className="h-8 w-full rounded border border-border bg-background px-2 text-sm" value={m[k]} onChange={(e) => setM({ ...m, [k]: Math.min(90, Math.max(0, Number(e.target.value) || 0)) })} /></label>;
    return (
        <div role="dialog" aria-modal="true" aria-label="Crop picture" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="w-full max-w-md space-y-3 rounded-xl border bg-card p-5 text-sm shadow-xl" onKeyDown={(e) => { if (e.key === "Escape") onClose(); e.stopPropagation(); }}>
                <h2 className="text-base font-semibold">Crop picture</h2>
                <div className="relative mx-auto w-fit overflow-hidden rounded border bg-muted" style={{ maxWidth: "100%" }}>
                    <img alt="" src={url} className="block max-h-56 max-w-full" />
                    <div className="pointer-events-none absolute inset-0 border-2 border-dashed border-red-600" style={{ left: `${m.l}%`, top: `${m.t}%`, right: `${m.r}%`, bottom: `${m.b}%`, boxShadow: "0 0 0 999px rgba(0,0,0,0.45)" }} />
                </div>
                <div className="grid grid-cols-4 gap-2">{num("l", "Left")}{num("t", "Top")}{num("r", "Right")}{num("b", "Bottom")}</div>
                {err ? <p role="alert" className="text-xs text-red-600">{err}</p> : null}
                <div className="flex justify-end gap-2">
                    <button type="button" className="h-8 rounded-md border px-3 hover:bg-accent" onClick={onClose}>Cancel</button>
                    <button type="button" data-crop-apply className="h-8 rounded-md bg-primary px-3 font-medium text-primary-foreground" onClick={async () => setErr((await onApply([m.l / 100, m.t / 100, 1 - m.r / 100, 1 - m.b / 100])) ?? null)}>Crop</button>
                </div>
            </div>
        </div>
    );
}

function BarButton({
    icon: Icon, children, onClick, disabled, primary, title
}: {
    icon: LucideIcon;
    children: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    primary?: boolean;
    title?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={cn(
                "flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
                primary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "hover:bg-accent"
            )}
        >
            <Icon className="h-4 w-4" aria-hidden />
            {children}
        </button>
    );
}

/**
 * "Saved to Drive" / "Saving…" / "Unsaved changes" — as in Docs, Sheets and Slides.
 * Rendered in the suite top bar, so colours come from the bar (accent, light or
 * dark style): opacity for secondary text, suite-appbar-btn for the retry button.
 */
function StatusBadge({
    status, loading, doc, inPlace, onRetry
}: {
    status: SaveStatus;
    loading: boolean;
    doc: OpenDoc | null;
    inPlace: boolean;
    onRetry: () => void;
}) {
    const base = "flex items-center gap-1 whitespace-nowrap text-xs opacity-80";
    if (loading) return <span className={base}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Opening…</span>;
    if (!doc) return null;
    switch (status) {
        case "saving":
            return <span className={base}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Saving…</span>;
        case "failed":
            return (
                <button
                    type="button"
                    onClick={onRetry}
                    className="suite-appbar-btn flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium underline-offset-2 hover:underline"
                >
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />Save failed — retry
                </button>
            );
        case "dirty":
            return <span className={base}><CircleDot className="h-3.5 w-3.5" aria-hidden />Unsaved changes</span>;
        default:
            if (!doc.drive) return <span className={base}>Not saved to Drive</span>;
            return inPlace
                ? <span className={base}><Check className="h-3.5 w-3.5" aria-hidden />Saved to Drive</span>
                : <span className={base} title={`Shared by ${doc.drive.ownerEmail ?? "someone else"}`}>View only — Save as to keep a copy</span>;
    }
}

function saveBlobToComputer(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error("Failed to read blob"));
        reader.readAsDataURL(blob);
    });
}

/** Natural pixel size of an image data URL. */
function imageSize(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth || 200, height: img.naturalHeight || 200 });
        img.onerror = () => reject(new Error("That image couldn't be read."));
        img.src = dataUrl;
    });
}

function stripExtension(name: string): string {
    const trimmed = (name ?? "").trim();
    if (!trimmed) return "Untitled";
    return trimmed.replace(/\.[^./\\]+$/, "");
}
